import { useEffect, useState } from "react";
import Cookies from "js-cookie";
import { TEAM_LIST, MATTER_STATUS, MATTERS_LIST } from "../../global/API";
import { Skeleton } from "primereact/skeleton";
import CustomModal from "../customModal/customModal";
import CustomChart from "../customChart/customChart";
import BoardData from "../boards/boardData";
import { formatDateTime, formatDateTimeShort, formatUSPhoneNumber, timeToSeconds } from "../globalFunctions/globalFunctions";
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, horizontalListSortingStrategy, arrayMove } from "@dnd-kit/sortable";
import SortableColumn from "../sortableColumn/sortableColumn";

const PipelineCategoryView = ({ pipelineData, setPipelineData, category, filteredValues, clioUserList, setClioUserList, matterStatusList, setMatterStatusList, user }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [loadingColumns, setLoadingColumns] = useState({});
  const [viewPipeline, setViewPipeline] = useState([]);
  const [getModalLayout, setGetModalLayout] = useState(null);
  const [activityTab, setActivityTab] = useState("zoomInfo");
  const [zoomFilter, setZoomFilter] = useState("");
  const [columnOrder, setColumnOrder] = useState([]);

  const getData = async (status = null, page = 1) => {
    const jwtToken = Cookies.get("jwtToken");
    const { searchValue, assignedToValue, typeFilter } = filteredValues;
    const options = {
      method: "GET",
      headers: { Authorization: `Bearer ${jwtToken}`, Accept: "application/json" },
    };

    let query = `?limit=10&page=${page}&status_category=${category}${status ? "&status=" + status : ""}${searchValue && status ? "&search=" + searchValue : ""}${
      assignedToValue && status ? "&assignedto=" + assignedToValue : ""
    }${typeFilter && status ? "&type=" + typeFilter : ""}`;

    const URL = MATTERS_LIST + query;

    try {
      const response = await fetch(URL, options);
      if (response.ok) {
        const data = await response.json();
        return data;
        // setPipelineData((previousState) => ({ ...previousState, status_list: data.status_list }));
      }
    } catch (error) {
      console.log(error);
    }
  };

  const fetchData = async () => {
    setIsLoading(true);
    const statusList =
      category === "intake"
        ? {
            status_list: [{ status: "Pending Contact" }, { status: "Consult Scheduled" }, { status: "Pending Retained" }, { status: "Pending Engagement" }, { status: "Unqualified" }],
          }
        : await getData();
    const updatedStatusList = statusList.status_list.map((eachStatus, idx) => ({ ...eachStatus, order: idx }));
    const totalData = {};
    const statusData = await Promise.all(
      statusList?.status_list.map(async (item) => {
        totalData[item?.status.replace(/\s+/g, "")] = await getData(item?.status);
      })
    );
    setPipelineData(() => ({ statusList: updatedStatusList, ...totalData }));
    setColumnOrder(updatedStatusList.map((s) => s.status)); // to track order
    setIsLoading(false);
  };

  const fetchAssignedToData = async () => {
    if (user?.role !== 1) return;
    const jwtToken = Cookies.get("jwtToken");

    const options = {
      method: "GET",
      headers: { Authorization: `Bearer ${jwtToken}`, Accept: "application/json" },
    };

    let query = `?status=1`;
    const URL = TEAM_LIST + query;
    try {
      const response = await fetch(URL, options);
      const data = await response.json();
      if (data.status === "Success") {
        setClioUserList(data.data);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const fetchMatterStatusData = async () => {
    const jwtToken = Cookies.get("jwtToken");

    const options = {
      method: "GET",
      headers: { Authorization: `Bearer ${jwtToken}`, Accept: "application/json" },
    };

    const URL = MATTER_STATUS;
    try {
      const response = await fetch(URL, options);
      const data = await response.json();
      if (response.ok) {
        setMatterStatusList(data);
      }
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    fetchData();
  }, [category]);

  useEffect(() => {
    fetchAssignedToData();
    fetchMatterStatusData();
  }, []);

  useEffect(() => {
    const { searchValue } = filteredValues;
    if (searchValue?.length >= 3 || searchValue === "") {
      const timeout = setTimeout(() => {
        fetchData();
      }, 300);
      return () => clearTimeout(timeout);
    }
  }, [filteredValues.searchValue]);

  useEffect(() => {
    fetchData();
  }, [filteredValues.assignedToValue, filteredValues.typeFilter]);

  const handleScroll = async (e, status) => {
    const box = e.target;

    if (box) {
      const isAtBottom = box.scrollHeight === box.scrollTop + box.clientHeight;
      if (isAtBottom) {
        const statusKey = status.replace(/\s+/g, "");
        const nextPage = pipelineData[statusKey].pagination.current_page + 1;
        const lastPage = pipelineData[statusKey].pagination.last_page;
        if (nextPage <= lastPage && !loadingColumns[statusKey]) {
          setLoadingColumns((prev) => ({ ...prev, [statusKey]: true }));
          const updatedData = await getData(status, nextPage);
          setPipelineData((prevState) => ({ ...prevState, [statusKey]: { ...updatedData, data: [...prevState[statusKey].data, ...updatedData.data] } }));
          setLoadingColumns((prev) => ({ ...prev, [statusKey]: false }));
        }
      }
    }
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      const oldIndex = columnOrder.indexOf(active.id);
      const newIndex = columnOrder.indexOf(over.id);

      const newOrder = arrayMove(columnOrder, oldIndex, newIndex);
      setColumnOrder(newOrder);
    }
  };

  const handleReset = () => {
    setZoomFilter("");
  };

  const chartOverview = (data = viewPipeline) => {
    const modifiedLeadData = data.map((lead) => {
      const inBound = { data: [], labels: [] };
      const outBound = { data: [], labels: [] };
      lead.client_zoom_calls.forEach((call) => {
        const seconds = timeToSeconds(call.duration);
        if (seconds <= 0) return;
        const label = `${formatDateTimeShort(call.start_time)}<br />Dur - ${call.duration}`;

        if (call.direction === "inbound") {
          inBound.data.push(seconds);
          inBound.labels.push(label);
        } else if (call.direction === "outbound") {
          outBound.data.push(seconds);
          outBound.labels.push(label);
        }
      });
      return { ...lead, inBoundChartData: inBound, outBoundChartData: outBound };
    });
    setViewPipeline(modifiedLeadData);
  };

  useEffect(() => {
    chartOverview();
  }, [zoomFilter]);

  const leadFilters = () => {
    return (
      <div className="page-title-box-content">
        <button type="button" className="page-title-btn refresh-button" title="Reset" onClick={handleReset}>
          <i className="ri-refresh-line"></i>
        </button>
        <div className="app-search">
          <select className="form-select" value={zoomFilter} onChange={(e) => setZoomFilter(e.target.value)}>
            <option value="">All</option>
            <option value="inbound">Inbound</option>
            <option value="outbound">Outbound</option>
            <option value="No Answer">No Answer</option>
          </select>
        </div>
      </div>
    );
  };

  const renderLeadDetails = (item) => {
    const {
      id,
      client_name,
      type,
      description,
      created_at,
      client_contact,
      status,
      status_category,
      client_zoom_calls,
      lead_source_name,
      Inbound,
      Outbound,
      Noanswer,
      Total_Calls,
      assignedto,
      inBoundChartData,
      outBoundChartData,
    } = item;

    const assignedToVal = clioUserList && clioUserList.filter((user) => user.id === assignedto)[0];

    const filteredZoomCalls = client_zoom_calls.filter((zoomCall) => zoomCall.direction.includes(zoomFilter) || zoomCall.call_result.includes(zoomFilter));

    const clientCalls = client_zoom_calls.filter((zoomCall) => zoomCall.direction === "outbound");
    const inboundClientCalls = client_zoom_calls.filter((zoomCall) => zoomCall.direction === "inbound");

    const outProgressWidth = clientCalls.length === 0 ? "4px" : `${(clientCalls.length / 12) * 100}%`;
    const outBarColor = clientCalls.length === 0 ? "red" : clientCalls.length <= 4 ? "red" : clientCalls.length <= 8 ? "orange" : "green";
    const inProgressWidth = inboundClientCalls.length === 0 ? "4px" : `${(inboundClientCalls.length / 12) * 100}%`;
    const inBarColor = inboundClientCalls.length === 0 ? "red" : inboundClientCalls.length <= 4 ? "red" : inboundClientCalls.length <= 8 ? "orange" : "green";

    const OutTotal = Outbound > 12 ? Outbound : 12;
    const InTotal = Inbound > 12 ? Inbound : 12;
    const NoAnswer = Noanswer > 12 ? Noanswer : 12;
    const TotalCalls = Total_Calls > 12 ? Total_Calls : 12;

    return (
      <>
        <div id="popupSidebar" className="popup-sidebar lead-sidebar">
          <ul>
            <li>
              <span>Name</span> {client_name}
            </li>
            <li>
              <span>Phone</span> {client_contact ? <a href={`tel:+1${client_contact}`}>{formatUSPhoneNumber(client_contact)}</a> : "--"}
            </li>
            <li>
              <span>Matters</span> {type}
            </li>
            <li>
              <span>Status</span> {status}
            </li>
            <li>
              <span>Assigned to</span> {assignedToVal ? `${assignedToVal?.name}` : "--"}
            </li>
            <li>
              <span>Source</span> {lead_source_name}
            </li>
            <li>
              <span>Created</span> {formatDateTime(created_at)}
            </li>
          </ul>
          <div className="des">
            <span>Description</span> <p>{description}</p>
          </div>

          {zoomFilter !== "No Answer" && (inBoundChartData?.data?.length > 0 || outBoundChartData?.data?.length > 0) && (
            <div className="des">
              {(zoomFilter === "inbound" || zoomFilter === "") && inBoundChartData?.data?.length > 0 && (
                <>
                  <span>Inbound Call Duration</span>
                  <div className="chart-grid">
                    <div className="chart-data">
                      <p>
                        {Inbound}/{InTotal}
                      </p>
                      <CustomChart chartLabels={inBoundChartData?.labels} chartDetails={inBoundChartData?.data} />
                    </div>
                    <ul className="chart-list">
                      <li>
                        <span>Total : </span>
                        {InTotal}
                      </li>
                      <li>
                        <span>Left : </span>
                        {InTotal - Inbound}
                      </li>
                      <li>
                        <span>Completed : </span>
                        {Inbound}
                      </li>
                    </ul>
                  </div>
                  <span>Inbound Call Progress</span>
                  <div style={{ display: "flex", alignItems: "center", gap: "20px", marginBottom: "15px" }}>
                    <div style={{ width: "230px", height: "12px", borderRadius: "10px", backgroundColor: "#f2eded", overflow: "hidden" }}>
                      <i
                        className="progress-bar-animate"
                        style={{
                          "--target-width": inProgressWidth,
                          "--bar-color": inBarColor,
                          display: "block",
                        }}
                      ></i>
                    </div>
                    <p style={{ marginBottom: "0" }}>
                      {inboundClientCalls.length}/{inboundClientCalls.length > 12 ? inboundClientCalls.length : 12}
                      {/* {filteredZoomCalls.length}/{filteredZoomCalls.length > 12 ? filteredZoomCalls.length : 12} */}
                    </p>
                  </div>
                </>
              )}
              {(zoomFilter === "outbound" || zoomFilter === "") && outBoundChartData?.data?.length > 0 && (
                <>
                  <span>Outbound Call Duration</span>
                  <div className="chart-grid">
                    <div className="chart-data">
                      <p>
                        {Outbound}/{OutTotal}
                      </p>
                      <CustomChart chartLabels={outBoundChartData?.labels} chartDetails={outBoundChartData?.data} />
                    </div>
                    <ul className="chart-list">
                      <li>
                        <span>Total : </span>
                        {OutTotal}
                      </li>
                      <li>
                        <span>Left : </span>
                        {OutTotal - Outbound}
                      </li>
                      <li>
                        <span>Completed : </span>
                        {Outbound}
                      </li>
                    </ul>
                  </div>
                  <span>Outbound Call Progress</span>
                  <div style={{ display: "flex", alignItems: "center", gap: "20px", marginBottom: "15px" }}>
                    <div style={{ width: "230px", height: "12px", borderRadius: "10px", backgroundColor: "#f2eded", overflow: "hidden" }}>
                      <i
                        className="progress-bar-animate"
                        style={{
                          "--target-width": outProgressWidth,
                          "--bar-color": outBarColor,
                          display: "block",
                        }}
                      ></i>
                    </div>
                    <p style={{ marginBottom: "0" }}>
                      {clientCalls.length}/{clientCalls.length > 12 ? clientCalls.length : 12}
                      {/* {filteredZoomCalls.length}/{filteredZoomCalls.length > 12 ? filteredZoomCalls.length : 12} */}
                    </p>
                  </div>
                </>
              )}
            </div>
          )}
          <div className="des des-list">
            <span>Call Information</span>
            <ul>
              <li>
                Inbound <span>{Inbound}</span>
              </li>
              <li>
                Outbound <span>{Outbound}</span>
              </li>
              <li>
                No Answer <span>{Noanswer}</span>
              </li>
            </ul>
          </div>
        </div>
        <div id="popupContentGrid" className="popup-content-grid">
          <div id="activitySidebar" className="activity-bar">
            {activityTab === "zoomInfo" && (
              <>
                <table className="table align-middle text-nowrap table-hover table-centered property-table ">
                  <thead className="bg-light-subtle">
                    <tr>
                      <th style={{ width: "10px", paddingRight: "0" }}>
                        <div className="form-check">
                          <input type="checkbox" className="form-check-input" />
                          <label className="form-check-label"></label>
                        </div>
                      </th>
                      <th>From</th>
                      <th>To</th>
                      <th>Start Time</th>
                      <th>Duration</th>
                      <th>Recorded</th>
                      <th>Direction</th>
                    </tr>
                  </thead>

                  <tbody>
                    {(filteredZoomCalls || []).length > 0 ? (
                      filteredZoomCalls.map((call) => (
                        <tr key={call.id}>
                          <td>
                            <div className="form-check">
                              <input type="checkbox" className="form-check-input" />
                            </div>
                          </td>
                          <td className="capitalize">
                            {call.from_name ? <span className="clio-name">{call.from_name.toLowerCase()}</span> : ""}
                            <br />
                            <a href={`tel:+1${call.from_phone_number}`}>{formatUSPhoneNumber(call.from_phone_number)}</a> &nbsp; {call.from_ext ? <>Ext.{call.from_ext}</> : ""}
                          </td>
                          <td className="capitalize">
                            {call.to_name ? <span className="clio-name">{call.to_name.toLowerCase()}</span> : ""}
                            <br />
                            <a href={`tel:+1${call.to_phone_number}`}>{formatUSPhoneNumber(call.to_phone_number)}</a> &nbsp; {call.to_ext ? <>Ext.{call.to_ext}</> : ""}
                          </td>
                          <td>{formatDateTime(call.start_time)}</td>
                          <td>{call.duration || "--"}</td>
                          <td>{call.recorded === "y" ? "Yes" : "No"}</td>
                          <td>{call.direction}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="7" style={{ textAlign: "center" }}>
                          No zoom call data found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </>
            )}
            {activityTab === "moreInfo" && <h4>Notes</h4>}
          </div>
        </div>
        <div className="activity-bar-ribbon">
          <div className="activity-list">
            <button
              onClick={() => {
                setActivityTab("zoomInfo");
              }}
              className={`activityInfo-button fadeUp ${activityTab === "zoomInfo" ? "active" : ""}`}
              style={{ animationDelay: "0.5s" }}
            >
              <i className="ri-phone-fill"></i>
              <span>Calls</span>
            </button>
            <button
              onClick={() => {
                setActivityTab("moreInfo");
              }}
              className={`activityInfo-button fadeUp ${activityTab === "moreInfo" ? "active" : ""}`}
              style={{ animationDelay: "0.8s" }}
            >
              <i className="ri-add-line"></i>
              <span>Notes</span>
            </button>
          </div>
        </div>
      </>
    );
  };

  return (
    <div className="board-grid">
      <DndContext sensors={useSensors(useSensor(PointerSensor))} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        {pipelineData?.statusList && !isLoading ? (
          <SortableContext items={columnOrder} strategy={horizontalListSortingStrategy}>
            {columnOrder.map((statusKey) => {
              const statusData = pipelineData.statusList.find((s) => s.status === statusKey);
              return (
                <SortableColumn
                  key={statusKey}
                  id={statusKey}
                  item={statusData}
                  pipelineData={pipelineData}
                  handleScroll={handleScroll}
                  chartOverview={chartOverview}
                  loadingColumns={loadingColumns}
                  viewPipeline={viewPipeline}
                  setViewPipeline={setViewPipeline}
                  clioUserList={clioUserList}
                  setClioUserList={setClioUserList}
                />
              );
            })}
          </SortableContext>
        ) : (
          // pipelineData.statusList.map((status, index) => (
          //   <BoardData
          //     key={index}
          //     item={status}
          //     pipelineData={pipelineData}
          //     handleScroll={handleScroll}
          //     chartOverview={chartOverview}
          //     loadingColumns={loadingColumns}
          //     viewPipeline={viewPipeline}
          //     setViewPipeline={setViewPipeline}
          //     clioUserList={clioUserList}
          //     setClioUserList={setClioUserList}
          //   />
          // ))
          <>
            <div className="card-board">
              <div className="card-board-header">
                <h5 className="header-title">
                  <Skeleton width="130px" height="18px"></Skeleton>
                </h5>
                <div className="header-right">
                  <span>
                    <Skeleton width="40px" height="18px"></Skeleton>
                  </span>
                </div>
              </div>
              <div className="card-board-body">
                {Array.from({ length: 3 }).map((_, index) => (
                  <Skeleton key={index} width="100%" height="200px" className="mb-2"></Skeleton>
                ))}
              </div>
            </div>
            <div className="card-board">
              <div className="card-board-header">
                <h5 className="header-title">
                  <Skeleton width="130px" height="18px"></Skeleton>
                </h5>
                <div className="header-right">
                  <span>
                    <Skeleton width="40px" height="18px"></Skeleton>
                  </span>
                </div>
              </div>
              <div className="card-board-body">
                {Array.from({ length: 3 }).map((_, index) => (
                  <Skeleton key={index} width="100%" height="200px" className="mb-2"></Skeleton>
                ))}
              </div>
            </div>
            <div className="card-board">
              <div className="card-board-header">
                <h5 className="header-title">
                  <Skeleton width="130px" height="18px"></Skeleton>
                </h5>
                <div className="header-right">
                  <span>
                    <Skeleton width="40px" height="18px"></Skeleton>
                  </span>
                </div>
              </div>
              <div className="card-board-body">
                {Array.from({ length: 3 }).map((_, index) => (
                  <Skeleton key={index} width="100%" height="200px" className="mb-2"></Skeleton>
                ))}
              </div>
            </div>
            <div className="card-board">
              <div className="card-board-header">
                <h5 className="header-title">
                  <Skeleton width="130px" height="18px"></Skeleton>
                </h5>
                <div className="header-right">
                  <span>
                    <Skeleton width="40px" height="18px"></Skeleton>
                  </span>
                </div>
              </div>
              <div className="card-board-body">
                {Array.from({ length: 3 }).map((_, index) => (
                  <Skeleton key={index} width="100%" height="200px" className="mb-2"></Skeleton>
                ))}
              </div>
            </div>
            <div className="card-board">
              <div className="card-board-header">
                <h5 className="header-title">
                  <Skeleton width="130px" height="18px"></Skeleton>
                </h5>
                <div className="header-right">
                  <span>
                    <Skeleton width="40px" height="18px"></Skeleton>
                  </span>
                </div>
              </div>
              <div className="card-board-body">
                {Array.from({ length: 3 }).map((_, index) => (
                  <Skeleton key={index} width="100%" height="200px" className="mb-2"></Skeleton>
                ))}
              </div>
            </div>
          </>
        )}
      </DndContext>
      <div className="minimize-grid">
        {viewPipeline.length >= 1 &&
          viewPipeline.map((item) => (
            <CustomModal
              key={item.id}
              renderItem={() => renderLeadDetails(item)}
              title={"Lead Overview"}
              secondaryTitle={item.client_name}
              getModalLayout={setGetModalLayout}
              filters={leadFilters}
              visible={true}
              showLeftSidebarIcon={true}
              showLayoutIcon={viewPipeline.length === 1}
              onHandleClose={() => {
                const modifiedData = viewPipeline.filter((lead) => lead.id !== item.id);
                setViewPipeline(modifiedData);
                handleReset();
              }}
            />
          ))}
      </div>
    </div>
  );
};

export default PipelineCategoryView;
